// The "Block library" button of the Schedule toolbar, and its popover. The popover lists the library
// entries. Each row is a `@dnd-kit` draggable of the DndContext in DayPlansTab, which owns the drop.
// The open state lives in DayPlansTab too, because a phase change or a drop closes the popover.
//
// A click outside the popover and Esc close it. A hover on a row shows a read-only detail on the
// left. The detail hides CLOSE_DELAY_MS after the pointer leaves the row and the detail.
// The detail is a read-only copy of the row detail in BlockLibraryPicker.tsx. Merge the two later.
import { useEffect, useRef, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { Button, Input, Popover } from "antd";
import type { InputRef } from "antd";
import { BookOutlined, HolderOutlined } from "@ant-design/icons";
import { blockEntryMatches, getBlockEntryCreator, plural, summarizeBlockContent } from "@utils/rtp_utils";
import type { RtpBlockLibraryState } from "./rtpHooks";
import { BlockContentView } from "./BlockContentView";
import type { RtpBlockTemplate } from "./types";

const CLOSE_DELAY_MS = 150;
const POPUP_CLASS = "rtp-wz rtp-lib-bar-pop";
const DETAIL_CLASS = "rtp-wz rtp-lib-pop rtp-lib-detail-pop";

interface LibraryRowProps {
	entry: RtpBlockTemplate;
	canDrag: boolean;
	detailOpen: boolean;
	metricLabel: (key: string) => string;
	onHover: (entryId: string) => void;
	onLeave: () => void;
	onOpenEntry?: (entryId: string) => void;
}

const LibraryRow = ({ entry, canDrag, detailOpen, metricLabel, onHover, onLeave, onOpenEntry }: LibraryRowProps) => {
	const drag = useDraggable({ id: `lib-${entry.id}`, data: { entry }, disabled: !canDrag });
	// The draggable is a wrapper, not the Popover child, because the Popover would need a ref on
	// its child. The detail is a portal in the React tree of the wrapper, so its pointer events must
	// not start a drag.
	return (
		<div ref={drag.setNodeRef} {...drag.attributes} {...drag.listeners}>
			<Popover
				open={detailOpen}
				trigger={[]}
				placement="left"
				arrow={false}
				destroyOnHidden
				classNames={{ root: DETAIL_CLASS }}
				content={
					<div
						className="rtp-lib-detail"
						onPointerDown={(e) => e.stopPropagation()}
						onMouseEnter={() => onHover(entry.id)}
						onMouseLeave={onLeave}
					>
						<div className="rtp-lib-detail-title">{entry.title}</div>
						<div className="rtp-lib-detail-meta">Saved by {getBlockEntryCreator(entry)}</div>
						<BlockContentView content={entry.content} metricLabel={metricLabel} />
						{onOpenEntry ? (
							<div className="rtp-lib-detail-foot">
								<Button
									type="link"
									size="small"
									data-attr="settings.rtp-block-library.popover-open-tab"
									onClick={() => onOpenEntry(entry.id)}
								>
									Open in the Block Library tab
								</Button>
							</div>
						) : null}
					</div>
				}
			>
				<div
					className={`rtp-lib-row rtp-lib-row--bar${canDrag ? " rtp-lib-row--grab" : ""}${detailOpen ? " rtp-lib-row--sel" : ""}`}
					onMouseEnter={() => onHover(entry.id)}
					onMouseLeave={onLeave}
				>
					{canDrag ? <HolderOutlined className="rtp-lib-handle" /> : null}
					<div className="rtp-lib-text">
						<div className="rtp-lib-name">{entry.title}</div>
						<div className="rtp-lib-meta">
							{summarizeBlockContent(entry.content)} · {getBlockEntryCreator(entry)}
						</div>
					</div>
				</div>
			</Popover>
		</div>
	);
};

interface BlockLibraryPopoverProps {
	library: RtpBlockLibraryState;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	// A row is in a drag, or its drop is in a write. The popover fades, and the pointer passes
	// through it. Every day stays in view and can take the drop.
	faded: boolean;
	// Rows drag only when the user can add blocks to the phase.
	canDrag: boolean;
	metricLabel: (key: string) => string;
	// The link of the row detail. It closes the popover first. Without it, the detail has no link.
	onOpenEntry?: (entryId: string) => void;
}

export const BlockLibraryPopover = ({
	library,
	open,
	onOpenChange,
	faded,
	canDrag,
	metricLabel,
	onOpenEntry,
}: BlockLibraryPopoverProps) => {
	const { templates, loading } = library;
	const searchRef = useRef<InputRef>(null);
	const [query, setQuery] = useState("");
	const [detailId, setDetailId] = useState<string | null>(null);
	const hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

	useEffect(() => () => clearTimeout(hideTimer.current), []);

	// No detail in a closed popover, and none during a drag.
	useEffect(() => {
		if (!open || faded) setDetailId(null);
	}, [open, faded]);

	// Esc does not close the popover during a drag: it cancels the drag.
	useEffect(() => {
		if (!open || faded) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onOpenChange(false);
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [open, faded, onOpenChange]);

	const showDetail = (entryId: string) => {
		clearTimeout(hideTimer.current);
		setDetailId(entryId);
	};
	const hideDetailSoon = () => {
		clearTimeout(hideTimer.current);
		hideTimer.current = setTimeout(() => setDetailId(null), CLOSE_DELAY_MS);
	};
	const openEntry = onOpenEntry
		? (entryId: string) => {
				setDetailId(null);
				onOpenChange(false);
				onOpenEntry(entryId);
			}
		: undefined;

	const rows = templates.filter((entry) => blockEntryMatches(entry, query, metricLabel));
	const emptyText = templates.length === 0 ? "The block library is empty." : "No blocks match your search.";

	const panel = (
		<div className="rtp-lib-bar-panel">
			<div className="rtp-lib-bar-head">
				<span className="rtp-lib-bar-title">Block library</span>
				<span className="rtp-lib-bar-count">{plural(templates.length, "block")}</span>
			</div>
			<Input
				ref={searchRef}
				className="rtp-lib-search"
				placeholder="Search the library"
				value={query}
				onChange={(e) => {
					setQuery(e.target.value);
					setDetailId(null);
				}}
			/>
			{canDrag ? <div className="rtp-lib-bar-hint">Drag a block onto a day.</div> : null}
			<div className="rtp-lib-rows">
				{rows.length === 0 && !loading ? <div className="rtp-lib-empty">{emptyText}</div> : null}
				{rows.map((entry) => (
					<LibraryRow
						key={entry.id}
						entry={entry}
						canDrag={canDrag}
						detailOpen={detailId === entry.id}
						metricLabel={metricLabel}
						onHover={showDetail}
						onLeave={hideDetailSoon}
						onOpenEntry={openEntry}
					/>
				))}
			</div>
		</div>
	);

	return (
		<Popover
			open={open}
			trigger="click"
			placement="bottomRight"
			arrow={false}
			destroyOnHidden
			// Below antd Modal (1000): a dialog opened from the toolbar covers this popover.
			zIndex={999}
			content={panel}
			onOpenChange={onOpenChange}
			// The search box gets the focus once, when the popover is open. A ref function runs on
			// each render, so it would focus the box again on each render.
			afterOpenChange={(visible) => {
				if (visible) searchRef.current?.focus({ preventScroll: true });
			}}
			classNames={{ root: `${POPUP_CLASS}${faded ? " rtp-lib-bar-pop--faded" : ""}` }}
		>
			<Button
				className={`rtp-lib-bar-btn${open ? " rtp-lib-bar-btn--open" : ""}`}
				icon={<BookOutlined />}
				data-attr="settings.rtp-block-library.popover-toggle"
			>
				Block library
			</Button>
		</Popover>
	);
};
